import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { createProject, initProject, Project } from '../api/projectsApi';
import { setHomeMode } from '../utils/assistantPreferences';
import Header from '../components/Header';
import { useTheme } from '../hooks/useTheme';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';

export default function ProjectsPage() {
  const { projects, setCurrentProject, refreshProjects, loading } = useProject();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    setError('');
    try {
      const p = await createProject(name.trim(), description.trim());
      await initProject(p.id);
      await refreshProjects();
      setDialogOpen(false);
      setName('');
      setDescription('');
      setCurrentProject({ ...p, role: 'owner' });
      navigate(`/projects/${p.id}`);
    } catch (e: any) {
      setError(e.message || 'Ошибка создания пространства');
    } finally {
      setCreating(false);
    }
  };

  const handleOpen = (p: Project & { role?: string }) => {
    setCurrentProject(p as Project & { role: string });
    navigate(`/projects/${p.id}`);
  };

  const handleAssistantHome = () => {
    setHomeMode('assistant');
    navigate('/assistant');
  };

  const isDark = theme.name === 'dark';
  const pageBackground = isDark
    ? 'radial-gradient(circle at top left, rgba(37, 99, 235, 0.18), transparent 34%), #0f1419'
    : 'radial-gradient(circle at top left, rgba(37, 99, 235, 0.12), transparent 32%), #f5f7fa';
  const surface = isDark ? '#151b23' : '#ffffff';
  const surfaceSoft = isDark ? '#111827' : '#f8fafc';
  const border = isDark ? 'rgba(148, 163, 184, 0.18)' : 'rgba(148, 163, 184, 0.28)';
  const mutedText = isDark ? '#94a3b8' : '#64748b';
  const primaryActionSx = {
    minHeight: 42,
    px: 2.25,
    borderRadius: '8px',
    border: '1px solid #2563eb',
    backgroundColor: '#2563eb',
    color: '#ffffff',
    fontWeight: 700,
    fontSize: 14,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#1d4ed8',
      borderColor: '#1d4ed8',
      boxShadow: '0 10px 24px rgba(37, 99, 235, 0.28)',
    },
  };
  const secondaryActionSx = {
    minHeight: 42,
    px: 2,
    borderRadius: '8px',
    color: theme.toolbar.text,
    borderColor: border,
    textTransform: 'none',
    fontWeight: 700,
    '&:hover': {
      borderColor: '#2563eb',
      backgroundColor: isDark ? 'rgba(37, 99, 235, 0.12)' : 'rgba(37, 99, 235, 0.08)',
    },
  };

  return (
    <>
      <Header title="Aspect" height={56} />
      <Box sx={{ px: { xs: 2, md: 3 }, pt: '88px', pb: 4, background: pageBackground, minHeight: '100vh' }}>
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              mb: 3,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <Box>
              <Typography variant="h4" component="h1" sx={{ color: theme.toolbar.text, fontWeight: 800, letterSpacing: 0 }}>
                Пространства
              </Typography>
              <Typography sx={{ color: mutedText, mt: 0.75, fontSize: 14 }}>
                Выберите рабочее пространство или создайте новое для диаграмм и реестров.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<ManageSearchOutlinedIcon />}
                onClick={handleAssistantHome}
                sx={secondaryActionSx}
              >
                AI поиск
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
                sx={primaryActionSx}
              >
                Создать пространство
              </Button>
            </Box>
          </Box>

          {loading && <Typography sx={{ color: theme.toolbar.text }}>Загрузка...</Typography>}

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
            {projects.map((p, index) => (
              <Fade in timeout={220 + index * 50} key={p.id}>
                <Card
                  elevation={0}
                  sx={{
                    border: `1px solid ${border}`,
                    background: surface,
                    color: theme.toolbar.buttonText,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    transition: 'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: '#2563eb',
                      boxShadow: isDark ? '0 18px 42px rgba(0, 0, 0, 0.32)' : '0 18px 42px rgba(15, 23, 42, 0.12)',
                    },
                  }}
                >
                  <CardActionArea onClick={() => handleOpen(p as Project & { role?: string })} sx={{ height: '100%', p: 0 }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2.25 }}>
                      <Avatar
                        variant="rounded"
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: '8px',
                          background: isDark ? 'rgba(37, 99, 235, 0.18)' : 'rgba(37, 99, 235, 0.10)',
                          color: '#2563eb',
                          flexShrink: 0,
                        }}
                      >
                        <FolderOutlinedIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            color: theme.toolbar.text,
                            fontWeight: 700,
                            fontSize: 16,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                          }}
                        >
                          {p.name}
                        </Typography>
                        <Typography
                          sx={{
                            color: mutedText,
                            fontSize: 13,
                            mt: 0.5,
                            minHeight: 20,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {p.description || 'Без описания'}
                        </Typography>
                        <Chip
                          size="small"
                          label={`Роль: ${p.role || '—'}`}
                          sx={{
                            mt: 1.5,
                            height: 24,
                            borderRadius: '8px',
                            backgroundColor: surfaceSoft,
                            color: mutedText,
                            border: `1px solid ${border}`,
                            fontWeight: 600,
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Fade>
            ))}
            {!loading && projects.length === 0 && (
              <Box
                sx={{
                  gridColumn: '1 / -1',
                  border: `1px dashed ${border}`,
                  background: surface,
                  borderRadius: '8px',
                  p: 3,
                  color: mutedText,
                }}
              >
                У вас нет пространств. Создайте первое.
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Новое пространство</DialogTitle>
        <DialogContent>
          <TextField label="Название" value={name} onChange={(e) => setName(e.target.value)} fullWidth autoFocus margin="dense" required />
          <TextField label="Описание" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth margin="dense" multiline rows={2} />
          {error && (
            <Typography role="alert" sx={{ color: '#dc2626', mt: 1, fontSize: 13 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={creating || !name.trim()}
            sx={primaryActionSx}
          >
            {creating ? 'Создание...' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
